/* =========================================================================
   MapleWeld - collection description collapse  (2026-09-21)

   WHAT THIS DOES
   Every collection now carries 200-400 words of category copy. That copy is
   there to be found by search and read by the customer who wants it - but a
   customer who already knows they want a 0.035 contact tip should not have to
   scroll past six paragraphs to reach the grid. So the description collapses
   to two lines with a Read more control, and opens on demand.

   WHY IT IS BUILT IN JS RATHER THAN CSS
   Progressive enhancement, in the direction that matters. If this file fails
   to load or throws, the description renders in full - long, but complete and
   readable. The opposite arrangement, clamping in the stylesheet and relying
   on JS to open it, traps the content behind a button that may never work.
   Content is never hidden unless the thing that reveals it is already running.

   It also means the clamp is measured, not guessed: the exact line-height is
   read off the rendered element, so two lines is two lines at every
   breakpoint, and the control is not injected at all when the description is
   already short enough to fit.

   THE HEADING CONTRAST FIX AT THE BOTTOM
   Not cosmetic. The theme paints h2/h3 inside the description at #333 while
   the collection page background is rgb(15,20,25) - a contrast ratio of about
   1.2:1, against a WCAG AA minimum of 3:1 for large text. The section
   headings were effectively invisible. No collection had body copy before
   this month, so nothing had ever exposed it. The rule here is scoped to
   .mwk__desc only and inherits the description's own colour, which is a
   passing contrast. It is a local patch on a theme-wide bug: the same #333
   almost certainly applies to headings elsewhere on dark backgrounds, and
   that deserves a proper fix in the stylesheet.
   ========================================================================= */
(function () {
  'use strict';

  var LINES = 2;          // lines of description left visible when collapsed
  var STYLE_ID = 'mwk-desc-collapse-css';
  var LABEL_MORE = 'Read more';
  var LABEL_LESS = 'Show less';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      '.mwk__desc--clamp{overflow:hidden;position:relative;',
        'max-height:var(--mwk-desc-max,3.2em);}',
      '.mwk__desc--clamp > :first-child{margin-top:0;}',
      '.mwk__desc--clamp::after{content:"";position:absolute;left:0;right:0;bottom:0;',
        'height:1.4em;pointer-events:none;background:var(--mwk-desc-fade,none);}',
      '.mwk__desc--clamp.is-open{max-height:none;overflow:visible;}',
      '.mwk__desc--clamp.is-open::after{display:none;}',
      '.mwk__descmore{display:inline-flex;align-items:center;gap:.5em;',
        'margin:.7rem 0 0;padding:.15em 0;background:none;border:0;font:inherit;',
        'font-size:.9375rem;font-weight:600;color:inherit;opacity:.9;',
        'cursor:pointer;line-height:1.4;}',
      '.mwk__descmore:hover{opacity:1;text-decoration:underline;}',
      '.mwk__descmore:focus-visible{outline:2px solid currentColor;outline-offset:3px;border-radius:2px;}',
      '.mwk__descmore-i{display:block;width:.46em;height:.46em;',
        'border-right:2px solid currentColor;border-bottom:2px solid currentColor;',
        'transform:translateY(-.14em) rotate(45deg);transition:transform .18s ease;}',
      '.mwk__descmore.is-open .mwk__descmore-i{transform:translateY(.06em) rotate(-135deg);}',
      '@media (prefers-reduced-motion: reduce){.mwk__descmore-i{transition:none;}}',
      /* see THE HEADING CONTRAST FIX in the header comment */
      '.mwk__desc h2,.mwk__desc h3,.mwk__desc h4{color:inherit;}'
    ].join('');
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(css));
    document.head.appendChild(el);
  }

  /* The clamp cuts mid-sentence, which reads as broken rather than as more-
     to-come. A fade sells it - but only if it fades to the ACTUAL page
     colour, and this theme is dark. Walk up for the first non-transparent
     background and build the gradient from it; find nothing, emit no fade at
     all rather than a grey smear over the wrong colour. */
  function fadeFor(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var c = window.getComputedStyle(node).backgroundColor;
      if (c && c !== 'transparent' && c.indexOf('rgba(0, 0, 0, 0)') === -1) {
        var m = c.match(/rgba?\(([^)]+)\)/);
        if (!m) return null;
        var p = m[1].split(',');
        var r = parseInt(p[0], 10), g = parseInt(p[1], 10), b = parseInt(p[2], 10);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
        return 'linear-gradient(to bottom, rgba(' + r + ',' + g + ',' + b + ',0) 0%, rgb('
             + r + ',' + g + ',' + b + ') 92%)';
      }
      node = node.parentElement;
    }
    return null;
  }

  function lineHeightOf(el) {
    var cs = window.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.6;   // 'normal'
    return lh;
  }

  function init() {
    var desc = document.querySelector('.mwk__desc');
    if (!desc) return;

    injectStyle();

    var btn = null;
    var open = false;

    function maxPx() {
      return Math.round(lineHeightOf(desc) * LINES);
    }

    function build() {
      if (btn) return;
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mwk__descmore';
      btn.setAttribute('aria-expanded', 'false');
      if (!desc.id) desc.id = 'mwk-desc';
      btn.setAttribute('aria-controls', desc.id);
      btn.innerHTML = '<span class="mwk__descmore-t"></span><span class="mwk__descmore-i" aria-hidden="true"></span>';
      btn.querySelector('.mwk__descmore-t').textContent = LABEL_MORE;
      desc.parentNode.insertBefore(btn, desc.nextSibling);

      btn.addEventListener('click', function () {
        open = !open;
        desc.classList.toggle('is-open', open);
        btn.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.querySelector('.mwk__descmore-t').textContent = open ? LABEL_LESS : LABEL_MORE;
        /* Collapsing a long description from the bottom leaves the reader
           looking at whatever happened to scroll into view. Put the top of
           the description back on screen if it has gone above the fold. */
        if (!open && desc.getBoundingClientRect().top < 0) {
          desc.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      });
    }

    function teardown() {
      desc.classList.remove('mwk__desc--clamp', 'is-open');
      desc.style.removeProperty('--mwk-desc-max');
      desc.style.removeProperty('--mwk-desc-fade');
      if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
      btn = null;
      open = false;
    }

    function measure() {
      if (open) return;                 // never fight a reader who opened it
      var max = maxPx();

      // Measure unclamped, or scrollHeight just reports the clamp back.
      desc.classList.remove('mwk__desc--clamp');
      var full = desc.scrollHeight;

      if (full <= max + 4) { teardown(); return; }   // already short enough

      desc.style.setProperty('--mwk-desc-max', max + 'px');
      var fade = fadeFor(desc);
      if (fade) desc.style.setProperty('--mwk-desc-fade', fade);
      desc.classList.add('mwk__desc--clamp');
      build();
    }

    measure();

    /* Line-height and wrapping both change across breakpoints, so two lines
       at 1440px is not two lines at 375px. Re-measure on resize, debounced. */
    var t = null;
    window.addEventListener('resize', function () {
      if (t) clearTimeout(t);
      t = setTimeout(measure, 150);
    }, { passive: true });

    /* Webfonts land after first paint and change the line box under us. */
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(measure).catch(function () {});
    }
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  } catch (e) {}
}());
